import * as React from 'react'
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const alertVariants = cva('flex items-start gap-3 rounded-md border border-l-4 px-4 py-3 text-base text-text', {
  variants: {
    tone: {
      info: 'border-border border-l-primary bg-primary-subtle',
      success: 'border-border border-l-success bg-success-subtle',
      warning: 'border-border border-l-warning-icon bg-warning-subtle',
      error: 'border-border border-l-error bg-error-subtle',
    },
  },
  defaultVariants: { tone: 'info' },
})

const ICONS = { info: Info, success: CheckCircle2, warning: AlertTriangle, error: AlertCircle } as const
const ICON_COLORS = { info: 'text-primary', success: 'text-success', warning: 'text-warning', error: 'text-error' } as const

export interface AlertProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>, VariantProps<typeof alertVariants> {
  title?: React.ReactNode
  /** Optional trailing action, e.g. a Retry button. */
  action?: React.ReactNode
}

/** Inline banner. Errors use role="alert"; everything else role="status". */
function Alert({ className, tone = 'info', title, action, children, ...props }: AlertProps) {
  const t = tone ?? 'info'
  const Icon = ICONS[t]
  return (
    <div role={t === 'error' ? 'alert' : 'status'} className={cn(alertVariants({ tone: t }), className)} {...props}>
      <Icon className={cn('mt-0.5 size-4 shrink-0', ICON_COLORS[t])} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div>{children}</div> : null}
      </div>
      {action}
    </div>
  )
}

export { Alert, alertVariants }
